/**
 * Admin — Referidos (vista global).
 * Muestra todos los referidos del sistema (todos los doctores), con stats
 * agregados por estado + indicador de cita programada por el receptor.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  Send,
  Search,
  Stethoscope,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  Printer,
} from "@/components/icons";
import { Link } from "react-router-dom";
import { doctorLabel } from "../../lib/doctorLabel";
import AppleKpi from "../../components/AppleKpi";

const STATUS_META = {
  pendiente: { label: "Pendiente", pill: "bg-amber-100 text-amber-700", icon: Clock },
  aceptado: { label: "Aceptado", pill: "bg-blue-100 text-blue-700", icon: CheckCircle },
  completado: { label: "Completado", pill: "bg-blue-50 text-[#019A75]", icon: CheckCircle },
  rechazado: { label: "Rechazado", pill: "bg-red-100 text-red-700", icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function AdminReferrals() {
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    api
      .get(`/referrals/admin/all${params.toString() ? `?${params}` : ""}`)
      .then((r) => {
        setItems(r.data.items || []);
        setStats(r.data.stats || {});
      })
      .catch(() => {
        setItems([]);
        setStats({});
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [statusFilter]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const n = q.toLowerCase();
    return items.filter(
      (r) =>
        (r.patient_name || "").toLowerCase().includes(n) ||
        (r.from_doctor_name || "").toLowerCase().includes(n) ||
        (r.to_doctor_name || "").toLowerCase().includes(n) ||
        (r.reason || "").toLowerCase().includes(n) ||
        (r.external_specialty || "").toLowerCase().includes(n)
    );
  }, [q, items]);

  return (
    <div className="space-y-6" data-testid="admin-referrals">
      <div>
        <h1 className="text-3xl font-extrabold text-black dark:text-white tracking-tight">
          Referidos — Vista Global
        </h1>
        <p className="text-sm text-black/60 dark:text-white/60 mt-1">
          Panel de todos los referidos emitidos por los doctores de la
          plataforma, con estados y seguimiento de citas.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard
          label="Total"
          value={stats.total || 0}
          icon={Send}
          testid="stat-total"
        />
        <StatCard
          label="Pendientes"
          value={stats.pendiente || 0}
          icon={Clock}
          accent="amber"
          testid="stat-pendiente"
        />
        <StatCard
          label="Aceptados"
          value={stats.aceptado || 0}
          icon={CheckCircle}
          accent="blue"
          testid="stat-aceptado"
        />
        <StatCard
          label="Completados"
          value={stats.completado || 0}
          icon={CheckCircle}
          accent="emerald"
          testid="stat-completado"
        />
        <StatCard
          label="Con cita"
          value={stats.with_appointment || 0}
          icon={Calendar}
          accent="purple"
          testid="stat-with-appt"
        />
      </div>

      {/* Contenedor principal */}
      <div className="bg-white dark:bg-[#171717] rounded-[28px] border border-black/[0.06] dark:border-white/[0.08] p-6">
        {/* Filtros */}
        <div className="flex items-center gap-2 flex-wrap mb-5">
          <div className="flex-1 min-w-[240px] max-w-md bg-white dark:bg-[#0d0d0d] border border-[#E5E7EB] dark:border-white/[0.08] rounded-full flex items-center px-3 py-1.5">
            <Search className="w-4 h-4 text-black/40 dark:text-white/40 flex-shrink-0" />
            <input
              data-testid="admin-ref-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar paciente, doctor o motivo…"
              className="flex-1 bg-transparent px-2 py-1 outline-none text-sm text-black dark:text-white placeholder-black/40 dark:placeholder-white/40"
            />
          </div>
          <select
            data-testid="admin-ref-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-full bg-white dark:bg-[#0d0d0d] border border-[#E5E7EB] dark:border-white/[0.08] text-sm text-black dark:text-white"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="aceptado">Aceptados</option>
            <option value="completado">Completados</option>
            <option value="rechazado">Rechazados</option>
          </select>
        </div>

        {loading ? (
          <div className="py-16 text-center text-black/50 dark:text-white/50">
            Cargando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-black/[0.04] dark:bg-white/[0.06] mx-auto flex items-center justify-center mb-4">
              <Stethoscope className="w-6 h-6 text-black/40 dark:text-white/40" />
            </div>
            <div className="text-black dark:text-white font-bold">
              No hay referidos que coincidan con los filtros
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  {[
                    "Fecha",
                    "Paciente",
                    "Emisor",
                    "Destino",
                    "Motivo",
                    "Prioridad",
                    "Estado",
                    "Cita programada",
                    "",
                  ].map((h) => (
                    <th
                      key={h || "act"}
                      className="py-3 px-3 first:pl-6 last:pr-6 text-[11px] uppercase tracking-widest text-black/50 dark:text-white/40 font-medium"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <AdminReferralRow key={r.id} r={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent = "slate", testid }) {
  return (
    <AppleKpi
      testid={testid}
      icon={Icon}
      title={label}
      value={value}
    />
  );
}

function AdminReferralRow({ r }) {
  const status = STATUS_META[r.status] || STATUS_META.pendiente;
  const StatusIcon = status.icon;
  const isExternal = !r.to_doctor_id;
  const destName = isExternal
    ? r.external_doctor_name ||
      r.external_specialty ||
      "Especialista externo"
    : doctorLabel(r.to_doctor_name || "—");
  const destSub = isExternal
    ? [r.external_specialty, r.external_center].filter(Boolean).join(" · ")
    : r.to_doctor_specialty || "";
  const initials = (r.patient_name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <tr
      data-testid={`admin-ref-row-${r.id}`}
      className="border-t border-black/[0.06] dark:border-white/[0.06] text-black dark:text-white hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition"
    >
      <td className="py-4 px-3 pl-6 whitespace-nowrap font-bold">
        {fmtDate(r.created_at)}
      </td>
      <td className="py-4 px-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white dark:bg-[#0d0d0d] border border-[#E5E7EB] dark:border-white/[0.08] text-xs font-extrabold flex items-center justify-center">
            {initials}
          </div>
          <span className="font-bold truncate max-w-[180px]">
            {r.patient_name || "—"}
          </span>
        </div>
      </td>
      <td className="py-4 px-3 max-w-[180px]">
        <div className="font-semibold truncate">
          {doctorLabel(r.from_doctor_name || "—")}
        </div>
        {r.from_doctor_specialty && (
          <div className="text-xs text-black/50 dark:text-white/50 truncate">
            {r.from_doctor_specialty}
          </div>
        )}
      </td>
      <td className="py-4 px-3 max-w-[200px]">
        <div className="font-semibold truncate">{destName}</div>
        {destSub && (
          <div className="text-xs text-black/50 dark:text-white/50 truncate">
            {destSub}
          </div>
        )}
        {isExternal && (
          <span className="inline-block mt-0.5 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold bg-purple-100 text-purple-700">
            Externo
          </span>
        )}
      </td>
      <td className="py-4 px-3 max-w-[220px]">
        <div className="line-clamp-2">{r.reason}</div>
      </td>
      <td className="py-4 px-3">
        <span
          className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${
            r.urgency === "urgente"
              ? "bg-red-100 text-red-700"
              : "bg-black/[0.06] dark:bg-white/[0.10] text-black/70 dark:text-white/70"
          }`}
        >
          {r.urgency === "urgente" ? "Urgente" : "Rutina"}
        </span>
      </td>
      <td className="py-4 px-3">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${status.pill}`}
        >
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </span>
      </td>
      <td className="py-4 px-3 whitespace-nowrap">
        {r.follow_up_appointment ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold bg-blue-50 text-[#019A75] dark:bg-[#01BC8E]/15 dark:text-emerald-400">
            <Calendar className="w-3 h-3" />
            {fmtDate(r.follow_up_appointment.starts_at)}
          </span>
        ) : r.to_doctor_id ? (
          <span className="text-xs text-black/40 dark:text-white/40">
            Sin cita
          </span>
        ) : (
          <span className="text-xs text-black/30 dark:text-white/30">—</span>
        )}
      </td>
      <td className="py-4 pl-3 pr-6 text-right whitespace-nowrap">
        <Link
          to={`/referidos/${r.id}/imprimir`}
          target="_blank"
          title="Ver / Imprimir"
          className="w-8 h-8 rounded-xl inline-flex items-center justify-center hover:bg-black/[0.06] dark:hover:bg-white/[0.08] text-black/70 dark:text-white/70"
        >
          <Printer className="w-4 h-4" />
        </Link>
      </td>
    </tr>
  );
}
